import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { t } from '@vk-hr-tek/core/translations';

import { EmployeeAbsence } from '@app/gen/organization';
import {
  ContentSearchRequest,
  ContentSearchResponse,
  ContentSearchGroupsResponse,
  ContentSearchResponseNews,
  ContentSearchResponseComments,
  ContentSearchResponseEmployees,
  ContentSearchResponseSurveys,
  ContentSearchResponseSites_all,
  ContentSearchResponseFilestorages,
  ContentSearchResponseEvents,
  ContentSearchResponseCommunities,
  ContentSearchResponseCommunity_posts,
} from '@app/gen/search';

import { EmployeeAbsenceWithFormat } from '../types';
import { ContentSearchOptions } from '../ui/Header/SearchInput/SearchInput.types';

const DEFAULT_LIMIT_CATEGORY_ITEM = 3;
const DEFAULT_LIMIT_SINGLE_CATEGORY = 6;

const absenceNameList = {
  vacation: 'В отпуске',
  maternity_leave: 'В декретном отпуске',
  student_leave: 'В учебном отпуске',
  business_trip: 'В командировке',
  sick_leave: 'На больничном',
};

type SearchItem =
  | ContentSearchResponseNews
  | ContentSearchResponseComments
  | ContentSearchResponseEmployees
  | ContentSearchResponseSurveys
  | ContentSearchResponseSites_all
  | ContentSearchResponseFilestorages
  | ContentSearchResponseEvents
  | ContentSearchResponseCommunities
  | ContentSearchResponseCommunity_posts;

interface SearchGroupData {
  label: string;
  type: string;
  data: SearchItem;
}

export interface SearchGroupFilter {
  label: string;
  value: ContentSearchRequest['group'];
}

@injectable()
export class LayoutMapper {
  constructor(private format: FormatService) {}

  private absencesFormat = (
    absence: EmployeeAbsence,
  ): EmployeeAbsenceWithFormat => {
    const isCurrentMoment =
      new Date(absence.from_date).getTime() < new Date().getTime();

    const parts = [
      absenceNameList[absence.absence_group.nick] || absenceNameList.vacation,
    ];

    if (absence.from_date && !isCurrentMoment) {
      parts.push(`с ${this.format.toTimeString(absence.from_date)}`);
    }
    if (absence.to_date) {
      parts.push(`по ${this.format.toTimeString(absence.to_date)}`);
    }

    return {
      ...absence,
      isCurrentMoment,
      format: parts.join(' '),
    };
  };

  formatAbsences = (
    absences?: EmployeeAbsence[],
  ): EmployeeAbsenceWithFormat[] => {
    if (!absences) return [];
    return absences.map(this.absencesFormat);
  };

  processSearchGroups(
    groups: ContentSearchGroupsResponse['search_groups'] | null,
  ): SearchGroupFilter[] {
    if (!groups || groups.length === 0) {
      return [];
    }

    const allGroups: SearchGroupFilter[] = [
      {
        label: t('search.searchBarChips.news'),
        value: 'news',
      },
      {
        label: t('search.searchBarChips.comments'),
        value: 'comments',
      },
      {
        label: t('search.searchBarChips.employees'),
        value: 'employees',
      },
      {
        label: t('search.searchBarChips.communities'),
        value: 'communities',
      },
      {
        label: t('search.searchBarChips.posts'),
        value: 'community_posts',
      },
      {
        label: t('search.searchBarChips.surveys'),
        value: 'surveys',
      },
      {
        label: t('search.searchBarChips.sites'),
        value: 'sites_all',
      },
      {
        label: t('search.searchBarChips.files'),
        value: 'filestorages',
      },
      {
        label: t('search.searchBarChips.events'),
        value: 'events',
      },
    ];

    const groupsSet = new Set<string>(groups);

    const availableGroups = allGroups.filter(
      (group) => group.value && groupsSet.has(group.value),
    );

    return [
      ...(availableGroups.length > 1
        ? [
            {
              label: t('search.searchBarChips.all'),
              value: undefined,
            },
          ]
        : []),
      ...availableGroups,
    ];
  }

  processSearchItems(
    response: ContentSearchResponse,
    group: ContentSearchRequest['group'],
  ): ContentSearchOptions[] {
    const allGroups: SearchGroupData[] = [
      {
        label: t('search.searchBarChips.news'),
        type: 'news' as const,
        data: response.news,
      },
      {
        label: t('search.searchBarChips.comments'),
        type: 'comments' as const,
        data: response.comments,
      },
      {
        label: t('search.searchBarChips.employees'),
        type: 'employees' as const,
        data: response.employees,
      },
      {
        label: t('search.searchBarChips.communities'),
        type: 'communities' as const,
        data: response.communities,
      },
      {
        label: t('search.searchBarChips.posts'),
        type: 'community_posts' as const,
        data: response.community_posts,
      },
      {
        label: t('search.searchBarChips.surveys'),
        type: 'surveys' as const,
        data: response.surveys,
      },
      {
        label: t('search.searchBarChips.sites'),
        type: 'sites_all' as const,
        data: response.sites_all,
      },
      {
        label: t('search.searchBarChips.files'),
        type: 'filestorages' as const,
        data: response.filestorages,
      },
      {
        label: t('search.searchBarChips.events'),
        type: 'events' as const,
        data: response.events,
      },
    ];

    if (group) {
      const selectedGroup = allGroups.find((g) => g.type === group);

      return selectedGroup
        ? this.processGroup(selectedGroup, DEFAULT_LIMIT_SINGLE_CATEGORY)
        : [];
    }

    return allGroups.flatMap((visibleGroup) =>
      this.processGroup(visibleGroup, DEFAULT_LIMIT_CATEGORY_ITEM),
    );
  }

  private processGroup(
    config: SearchGroupData,
    limit: number,
  ): ContentSearchOptions[] {
    if (!config.data?.items?.length) {
      return [];
    }

    const title = {
      id: config.type,
      type: 'label' as const,
      label: config.label,
      total: config.data.total,
    };

    const items = config.data.items.map((item) => ({
      ...item,
      type: config.type,
    }));

    return [title, ...items.slice(0, limit)] as ContentSearchOptions[];
  }
}
